/**
 * @license
 * Copyright 2021 Google LLC. All Rights Reserved.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 * =============================================================================
 */
import * as poseDetection from '@tensorflow-models/pose-detection';
//import * as params from './params';
//import {isMobile} from '../util/tools';
import { INFO } from '../util/object'
import { UtilClass } from '../class/UtilClass';

export const DEFAULT_LINE_WIDTH = 2;
export const DEFAULT_RADIUS = 4;

// CONSTS.WEBCAM_BASE_ASPECT
// CONSTS.WEBCAM_BASE_HEIGHT
// CONSTS.WEBCAM_BASE_WIDTH_FN()
export const VIDEO_SIZE = {
  '444 X 240': {width: 444, height: 240},
  '500 X 270': {width: 500, height: 270},
  '666 X 360': {width: 666, height: 360},
  '888 X 480': {width: 888, height: 480},
};
export const STATE = {
  camera: {targetFPS: 30, sizeOption: '888 X 480'},
  backend: '',
  flags: {},
  modelConfig: {},
  model: poseDetection.SupportedModels.MoveNet
};
export const POSENET_CONFIG = {
  maxPoses: 1,
  scoreThreshold: 0.5
};
export const MOVENET_CONFIG = {
  maxPoses: 1,
  type: 'lightning',
  scoreThreshold: 0.3
};

const Util = new UtilClass();
export class CameraClass {

  constructor() {
    this.video = document.getElementById('video');
    this.vcanvas = document.getElementById('vcanvas');
    this.output = document.getElementById('output');
    //this.octx = this.vcanvas.getContext('2d');
    this.octx = this.output.getContext('2d');
  }

  /**
   * Initiate a Camera instance and wait for the camera stream to be ready.
   * @param cameraParam From app `STATE.camera`.
   */
  static async setupCamera(cameraParam) {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error(
          'Browser API navigator.mediaDevices.getUserMedia not available');
    }

    const {targetFPS, sizeOption} = cameraParam;
    const $size = VIDEO_SIZE[sizeOption];
    console.log("camera_setup.66:  START WEBCAM OBJECT SETUP... $size=",$size, " targetFPS=",targetFPS);
    const videoConfig = {
      'audio': false,
      'video': {
        facingMode: 'user',
        // Only setting the video to a specified size for large screen, on
        // mobile devices accept the default size.
        width: Util.isMobile() ? VIDEO_SIZE['888 X 480'].width : $size.width,
        height: Util.isMobile() ? VIDEO_SIZE['888 X 480'].height : $size.height,
        frameRate: {
          ideal: targetFPS,
        }
      }
    };

    const stream = await navigator.mediaDevices.getUserMedia(videoConfig);
    INFO.webcam_stream = stream;    // for tear down

    const camera = new CameraClass();
    camera.video.srcObject = stream;

    await new Promise((resolve) => {
      camera.video.onloadedmetadata = (video) => {
				console.log("camera_setup.89:  WEBCAM DATA LOADED!", video);
        resolve(video);
      };
    });

    camera.video.play();

    const videoWidth = camera.video.videoWidth;
    const videoHeight = camera.video.videoHeight;
    // Must set below two lines, otherwise video element doesn't show.
    camera.video.width = videoWidth;
    camera.video.height = videoHeight;

    camera.vcanvas.width = videoWidth;
    camera.vcanvas.height = videoHeight;
    const canvasContainer = document.querySelector('#video_container');
    canvasContainer.style = `width: ${videoWidth}px; height: ${videoHeight}px`;

	INFO.video_owidth = videoWidth;			// orig video file aspect ratio, this chgs later
	INFO.video_oheight = videoHeight;		// orig video file aspect ratio, this chgs later
	INFO.webcam_owidth = videoWidth;
	INFO.webcam_oheight = videoHeight;

    // Because the image from camera is mirrored, need to flip horizontally.
    //camera.octx.translate(camera.video.videoWidth, 0);        // not working
    //camera.octx.scale(-1, 1);

    return camera;
  }  

  async stopCamera() {
      if ((INFO.webcam_stream !== undefined) && (INFO.webcam_stream !== null)) {
          INFO.webcam_stream.getTracks()[0].stop();
      }
      console.log("stop_camera.119:  CAMERA STOPPED  stream=", INFO.webcam_stream);

      this.video.pause();
      this.video.srcObject = null;
      this.video.onloadedmetadata = null;
      INFO.webcam_stream = null;
      INFO.webcam = null;
      delete this;      // trigger GC
  }
 
  drawCtx() {
      //console.log("drawctx.113:  Drawing CTX  v.w=",this.video.videoWidth, "  v.h=",this.video.videoHeight);
      //this.octx.drawImage(
      //    this.video, 0, 0, this.video.videoWidth, this.video.videoHeight);

      //var canvas0 = document.getElementById('output');
      //const ctx = canvas0.getContext('2d');
      //canvas0.width = width
      //canvas0.height = height;

      var width = this.vcanvas.width;
      var height = this.vcanvas.height;
      this.octx.drawImage(this.video, 0, 0, width, height);   // add video image first

  }

  clearCtx() {
    this.octx.clearRect(0, 0, this.video.videoWidth, this.video.videoHeight);
  }

  /**
   * Draw the keypoints and skeleton on the video.
   * @param poses A list of poses to render.
   */
  drawResults(poses) {
    for (const pose of poses) {
      this.drawResult(pose);
    }
  }

  /**
   * Draw the keypoints and skeleton on the video.
   * @param pose A pose with keypoints to render.
   */
  drawResult(pose) {
    if (pose.keypoints != null) {
      //console.log("draw_result.140:  pose=",pose);
      this.drawKeypoints(pose.keypoints);
      this.drawSkeleton(pose.keypoints);
    }
  }

  /**
   * Draw the keypoints on the video.
   * @param keypoints A list of keypoints.
   */
  drawKeypoints(keypoints) {
    const keypointInd = poseDetection.util.getKeypointIndexBySide(STATE.model);
    this.octx.fillStyle = 'White';
    this.octx.strokeStyle = 'White';
    this.octx.lineWidth = DEFAULT_LINE_WIDTH;

    for (const i of keypointInd.middle) {
      this.drawKeypoint(keypoints[i]);
    }

    this.octx.fillStyle = 'Green';
    for (const i of keypointInd.left) {
      this.drawKeypoint(keypoints[i]);
    }

    this.octx.fillStyle = 'Orange';
    for (const i of keypointInd.right) {
      this.drawKeypoint(keypoints[i]);
    }
  }

  drawKeypoint(keypoint) {
    // If score is null, just show the keypoint.
    const score = keypoint.score != null ? keypoint.score : 1;
    const scoreThreshold = STATE.modelConfig.scoreThreshold || 0;

    if (score >= scoreThreshold) {
      const circle = new Path2D();
      circle.arc(keypoint.x, keypoint.y, DEFAULT_RADIUS, 0, 2 * Math.PI);
      this.octx.fill(circle);
      this.octx.stroke(circle);
    }
  }

  /**
   * Draw the skeleton of a body on the video.
   * @param keypoints A list of keypoints.
   */
  drawSkeleton(keypoints) {
    this.octx.fillStyle = 'White';
    this.octx.strokeStyle = 'White';
    this.octx.lineWidth = DEFAULT_LINE_WIDTH;

    poseDetection.util
      .getAdjacentPairs(STATE.model)
      .forEach(([ i, j ]) => {
        const kp1 = keypoints[i];
        const kp2 = keypoints[j];

        // If score is null, just show the keypoint.
        const score1 = kp1.score != null ? kp1.score : 1;
        const score2 = kp2.score != null ? kp2.score : 1;
        const scoreThreshold = STATE.modelConfig.scoreThreshold || 0;

        if (score1 >= scoreThreshold && score2 >= scoreThreshold) {
          this.octx.beginPath();
          this.octx.moveTo(kp1.x, kp1.y);
          this.octx.lineTo(kp2.x, kp2.y);
          this.octx.stroke();
        }
    });
  }
}
